import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StudyPlan, OnboardingData } from '../types/onboarding';
import { generatePlan } from '../services/onboardingPlan';
import {
    ChevronRight,
    RotateCcw,
    BarChart3,
    ArrowLeft
} from 'lucide-react';
import { SidebarNew } from '../components/SidebarNew';
import { useAuth } from '../AuthContext';
import { getStudyState, saveStudyState } from '../services/studyPlanStore';
import { ScioCountdown } from '../components/ScioCountdown';
import { MissionTaskCard } from '../components/MissionTaskCard';
import { WeeklyProgress } from '../components/WeeklyProgress';
import { loadStreakFromFirestore } from '../utils/firestoreStreak';
import { getWeeklyActivityStatus } from '../services/activityService';
import { getPragueDate } from '../utils/pragueDate';
import { StreakData } from '../types';

const MyPlanPage: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [plan, setPlan] = useState<StudyPlan | null>(null);
    const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [streakData, setStreakData] = useState<StreakData | null>(null);
    const [weeklyActivity, setWeeklyActivity] = useState<boolean[]>(new Array(7).fill(false));

    const refreshData = async () => {
        if (!currentUser) return;
        try {
            const [state, streak, activity] = await Promise.all([
                getStudyState(currentUser.uid),
                loadStreakFromFirestore(currentUser.uid),
                getWeeklyActivityStatus(currentUser.uid)
            ]);

            if (state) {
                setPlan(state.studyPlan);
                setOnboardingData(state.onboardingData);
            }
            if (streak) setStreakData(streak);
            if (activity) setWeeklyActivity(activity);
        } catch (e) {
            console.error("Failed to load study state or streak", e);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    // Listen for completion events to update UI immediately
    useEffect(() => {
        const handleEvent = () => refreshData();
        window.addEventListener('taskCelebration', handleEvent);
        window.addEventListener('studyPlanUpdated', handleEvent);
        return () => {
            window.removeEventListener('taskCelebration', handleEvent);
            window.removeEventListener('studyPlanUpdated', handleEvent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser]);

    const user = currentUser ? {
        id: currentUser.uid,
        name: currentUser.displayName || 'User',
        email: currentUser.email || '',
        avatar: currentUser.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser.displayName || 'User')}&background=3b82f6&color=fff`,
        studyGoals: {
            dailyFlashcards: 20,
            weeklyHours: 5
        }
    } : null;

    const [viewOffset, setViewOffset] = useState(0);

    // Reset view offset when plan changes (e.g. new day)
    useEffect(() => {
        setViewOffset(0);
    }, [plan?.missionState?.dayIndex]);

    const handleStartMission = () => {
        if (plan && plan.todayMission.tasks.length > 0) {
            window.location.href = plan.todayMission.tasks[0].deepLink;
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleRegenerate = async () => {
        if (onboardingData && currentUser) {
            // Check if user has pro subscription (you'll need to implement this check)
            const isPro = false; // TODO: Replace with actual pro status check
            const { plan: newPlan } = generatePlan(onboardingData, isPro);
            setPlan(newPlan);

            // Clear daily completion flag to allow retaking mission if changed
            // Clear daily completion flag to allow retaking mission if changed
            const today = getPragueDate();
            localStorage.removeItem(`mission_completed_${today}`);

            try {
                await saveStudyState(currentUser.uid, {
                    studyPlan: newPlan
                });
            } catch (e) {
                console.error("Failed to save regenerated plan", e);
            }
        }
    };

    if (isLoading) {
        return (
            <div className="fixed inset-0 flex flex-col md:flex-row bg-light-bg dark:bg-dark-bg font-poppins text-foreground overflow-hidden">
                {user && <SidebarNew user={user} activeSection="plan" onNavigate={section => navigate(`/${section}`)} />}
                <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-orange"></div>
                </main>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="fixed inset-0 flex flex-col md:flex-row bg-light-bg dark:bg-dark-bg font-poppins text-foreground overflow-hidden">
                {user && <SidebarNew user={user} activeSection="plan" onNavigate={section => navigate(`/${section}`)} />}
                <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8 flex items-center justify-center">
                    <div className="text-center max-w-md w-full">
                        <div className="bg-brand-orange/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                            <BarChart3 className="w-8 h-8 text-brand-orange" />
                        </div>
                        <h1 className="text-2xl font-bold mb-3">Zatím nemáš žádný plán</h1>
                        <p className="text-muted-foreground mb-8 text-sm md:text-base">Vytvoř si personalizovaný studijní plán během 2 minut a začni se připravovat efektivně.</p>
                        <button
                            onClick={() => navigate('/onboarding')}
                            className="w-full py-4 bg-brand-orange text-white rounded-xl font-bold hover:bg-brand-orange/90 transition-all shadow-lg"
                        >
                            Vytvořit plán
                        </button>
                    </div>
                </main>
            </div>
        );
    }

    const trackLabel = plan.focusSplit.osp && plan.focusSplit.zsv ? 'Obojí' : plan.focusSplit.osp ? 'OSP' : 'ZSV';

    // Determine exam date based on user selection or fallback to nearest SCIO exam
    const getNearestExamDate = () => {
        const now = new Date();
        const futureDates = [
            new Date('2026-02-28'),
            new Date('2026-03-28'),
            new Date('2026-04-25'),
            new Date('2026-05-23')
        ].filter(d => d.getTime() >= now.getTime() - 24 * 60 * 60 * 1000); // include today
        return futureDates.length > 0 ? futureDates[0] : new Date('2026-03-28');
    };

    const examDate = onboardingData?.examDate
        ? new Date(onboardingData.examDate)
        : getNearestExamDate();

    // Task data simulation (completed logic needs to be integrated with real tracking)
    // For now we simulate that task 0 is in progress, task 1 not started, task 2 completed

    // Calculate mission progress for button logic
    const completedTasksCount = plan.todayMission.tasks.filter(t => plan.missionState?.completedTaskIds?.includes(t.id)).length;
    const totalTasksCount = plan.todayMission.tasks.length;
    const missionProgress = totalTasksCount > 0 ? (completedTasksCount / totalTasksCount) * 100 : 0;

    let buttonText = "Spustit dnešní misi →";
    if (missionProgress > 0 && missionProgress < 100) buttonText = "Pokračovat v misi →";
    if (missionProgress === 100) buttonText = "Mise splněna! ✓";

    // Fix: Ensure that if streak says "today active", the weekly progress shows it too
    // This handles the race condition where `weeklyActivity` (slow log) might lag behind `streakData` (fast live data)
    const todayPrague = getPragueDate();
    const isStreakActiveToday = streakData ? streakData.lastActivityDate === todayPrague : false;

    // Create a copy of the activity array to potentially override today
    const currentDayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1; // 0=Mon
    const displayActivity = [...weeklyActivity];

    if (isStreakActiveToday && currentDayIndex >= 0 && currentDayIndex < 7) {
        displayActivity[currentDayIndex] = true;
    }

    const completedDaysCount = displayActivity.filter(Boolean).length;

    // --- History & Preview Logic ---
    const getDisplayedMission = () => {
        if (viewOffset === 0) return plan.todayMission;

        if (viewOffset < 0) {
            // History
            const historyIndex = (plan.history?.length || 0) + viewOffset;
            if (historyIndex >= 0 && plan.history) {
                return plan.history[historyIndex];
            }
            // If out of bounds of history, return null or empty? 
            // Should be handled by UI navigation limits
            return null;
        }

        if (viewOffset > 0) {
            // Preview Future
            // Simulate generation. Logic: passing state with increased day index.
            // Note: This is a simplifiction. Real generation might depend on what happens 'today'.
            // But for preview it's fine.
            if (!onboardingData || !plan.missionState) return null;

            // We need to chain generation for +2 days
            let tempState = { ...plan.missionState };
            let tempPlan = plan;

            // Generate iteratively to get to the offset day
            for (let i = 0; i < viewOffset; i++) {
                const nextDayState = {
                    ...tempState,
                    dayIndex: tempState.dayIndex + 1
                };
                const res = generatePlan(onboardingData, false, nextDayState); // Assuming false for isPro in preview for now
                tempPlan = res.plan;
                // Update tempState for next iteration
                tempState = {
                    ...nextDayState,
                    // Simple rotation simulation
                    currentTopicRotation: (tempState.currentTopicRotation + 1) % 5,
                    // We don't really know completedChapters until they do it, 
                    // so we assume no new completions for preview or just allow repetition
                    completedChapters: tempState.completedChapters
                };
            }
            return tempPlan.todayMission;
        }

        return plan.todayMission;
    };

    const displayedMission = getDisplayedMission();
    const canGoBack = (plan.history?.length || 0) + viewOffset > 0;
    const canGoForward = viewOffset < 2; // Limit to 2 days preview

    return (
        <div className="fixed inset-0 flex flex-col md:flex-row bg-light-bg dark:bg-dark-bg font-poppins text-foreground overflow-hidden">
            {user && <SidebarNew user={user} activeSection="plan" onNavigate={section => navigate(`/${section}`)} />}

            <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
                <div className="max-w-[1100px] mx-auto space-y-8 pb-12">
                    {/* Nav Header */}
                    <div className="flex items-center gap-4 mb-4">
                        <button
                            onClick={() => navigate('/')}
                            className="p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground border border-transparent hover:border-border"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Můj studijní plán</h1>
                    </div>

                    {/* TOP SECTION: Combined Summary/Calendar & Countdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Combined Summary & Calendar Widget */}
                        <div className="lg:col-span-2 bg-card rounded-3xl p-6 border border-border flex flex-col shadow-sm min-h-[220px]">
                            {/* Header: Role Information & Regenerate Button */}
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-4">
                                    <span className="px-3 py-1 text-xs font-bold bg-brand-orange/10 text-brand-orange rounded-full border border-brand-orange/20 uppercase tracking-wide">
                                        {trackLabel}
                                    </span>
                                </div>
                                <button
                                    onClick={() => navigate('/onboarding')}
                                    className="flex items-center gap-2 px-3 py-1.5 bg-secondary text-foreground rounded-xl font-semibold hover:bg-secondary/80 transition-colors border border-border text-xs"
                                >
                                    <RotateCcw className="w-3.5 h-3.5 text-muted-foreground" />
                                    Regenerovat plán
                                </button>
                            </div>

                            {/* Embedded Calendar / Streak Widget */}
                            <div className="flex-1">
                                <WeeklyProgress
                                    completedDays={completedDaysCount}
                                    currentDayIndex={currentDayIndex} // 0=Mon
                                    weekDays={['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne']}
                                    dailyMinutes={plan.cadence.minutesPerDay}
                                    dailyCompletionStatus={displayActivity}
                                    embedded={true}
                                    currentStreak={streakData?.current || 0}
                                    hasStreakToday={isStreakActiveToday}
                                />
                            </div>
                        </div>

                        {/* SCIO Countdown Card */}
                        <div className="lg:col-span-1 h-full">
                            <ScioCountdown
                                examDate={examDate}
                                subjectName={trackLabel}
                            />
                        </div>
                    </div>

                    {/* BOTTOM SECTION: Mission & Focus Stats */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

                        {/* LEFT: Today's Mission (Wider: col-span-2) */}
                        <section className="lg:col-span-2 bg-card rounded-3xl p-6 md:p-8 border border-border shadow-sm flex flex-col relative overflow-hidden">
                            {/* Mission Header with Navigation */}
                            <div className="flex items-center justify-between mb-8 relative z-10">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => canGoBack && setViewOffset(o => o - 1)}
                                        disabled={!canGoBack}
                                        className={`p-1 rounded-full transition-colors ${canGoBack ? 'hover:bg-secondary text-brand-orange' : 'text-muted-foreground opacity-30 cursor-not-allowed'}`}
                                    >
                                        <ChevronRight className="w-6 h-6 rotate-180" />
                                    </button>

                                    <div className="flex flex-col">
                                        <h2 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                                            {viewOffset === 0 ? "Dnešní mise" :
                                                viewOffset < 0 ? "Historie úkolů" : "Náhled mise"}
                                            {viewOffset !== 0 && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full border ${viewOffset < 0 ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700' : 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800'}`}>
                                                    {viewOffset < 0 ? 'Minulost' : `+${viewOffset} dní`}
                                                </span>
                                            )}
                                        </h2>
                                        {displayedMission && (
                                            <span className="text-xs text-muted-foreground font-medium">
                                                Den {displayedMission.dayIndex}
                                            </span>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => canGoForward && setViewOffset(o => o + 1)}
                                        disabled={!canGoForward}
                                        className={`p-1 rounded-full transition-colors ${canGoForward ? 'hover:bg-secondary text-brand-orange' : 'text-muted-foreground opacity-30 cursor-not-allowed'}`}
                                    >
                                        <ChevronRight className="w-6 h-6" />
                                    </button>
                                </div>

                                <span className="text-sm font-bold text-muted-foreground bg-secondary px-3 py-1 rounded-full border border-border">
                                    {displayedMission?.estimatedMinutes || 0} min
                                </span>
                            </div>

                            <div className="flex-1 flex flex-col gap-4">
                                {!displayedMission ? (
                                    <div className="p-8 text-center text-muted-foreground">
                                        Mise pro tento den není k dispozici.
                                    </div>
                                ) : (
                                    displayedMission.tasks.map((task, idx) => {
                                        // For history (offset < 0), we might want to show actual completion status if we tracked it in the history object
                                        // For now, we use current global completion state for Today, and maybe checked for history if we assume it was done?

                                        // Use real completion status if available
                                        let status: 'completed' | 'in-progress' | 'not-started' = 'not-started';

                                        if (viewOffset === 0) {
                                            // Today
                                            const completedTaskIds = plan.missionState?.completedTaskIds || [];
                                            const isRealCompleted = completedTaskIds.includes(task.id);
                                            if (isRealCompleted) status = 'completed';
                                            // Demo overrides for "not started" logic if you want
                                        } else if (viewOffset < 0) {
                                            // History - Assume completed if it's in history? 
                                            // Or check if we have a record?
                                            // Since we didn't burn completion into history items, we can't know for sure without the `mergedCompletedIds` logic we added.
                                            // But plan.missionState.completedTaskIds NOW contains ALL history ids too!
                                            // So we can check against that!
                                            const completedTaskIds = plan.missionState?.completedTaskIds || [];
                                            const isRealCompleted = completedTaskIds.includes(task.id);
                                            if (isRealCompleted) status = 'completed';
                                        } else {
                                            // Future - Always not started
                                            status = 'not-started';
                                        }

                                        return (
                                            <MissionTaskCard
                                                key={task.id}
                                                task={{
                                                    ...task,
                                                    progress: status === 'completed' ? 100 : 0,
                                                    completed: status === 'completed'
                                                }}
                                                index={idx}
                                                status={status}
                                                onClick={() => {
                                                    if (viewOffset <= 0) window.location.href = task.deepLink;
                                                }}
                                                isLocked={viewOffset > 0} // Lock future tasks
                                            />
                                        );
                                    })
                                )}

                                {/* Bonus Tasks Section */}
                                {displayedMission && displayedMission.bonusTasks && displayedMission.bonusTasks.length > 0 && (
                                    <>
                                        <div className="flex items-center gap-2 mt-2 mb-2 px-2">
                                            <div className="h-[1px] flex-1 bg-border"></div>
                                            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Zbytek ze včerejška</span>
                                            <div className="h-[1px] flex-1 bg-border"></div>
                                        </div>
                                        {displayedMission.bonusTasks.map((task, idx) => {
                                            const completedTaskIds = plan.missionState?.completedTaskIds || [];
                                            const isCompleted = completedTaskIds.includes(task.id);

                                            // Status logic similarly to main tasks
                                            let status: 'completed' | 'in-progress' | 'not-started' = 'not-started';
                                            if (viewOffset <= 0 && isCompleted) status = 'completed';
                                            else if (viewOffset > 0) status = 'not-started';

                                            return (
                                                <MissionTaskCard
                                                    key={task.id}
                                                    task={{
                                                        ...task,
                                                        progress: status === 'completed' ? 100 : 0,
                                                        completed: status === 'completed'
                                                    }}
                                                    index={idx + displayedMission.tasks.length} // Continue index
                                                    status={status}
                                                    onClick={() => {
                                                        if (viewOffset <= 0) window.location.href = task.deepLink;
                                                    }}
                                                    isLocked={viewOffset > 0}
                                                    isBonus={true}
                                                />
                                            );
                                        })}
                                    </>
                                )}
                            </div>

                            <button
                                onClick={handleStartMission}
                                disabled={missionProgress === 100}
                                className={`
                                    w-full mt-8 py-4 rounded-2xl font-bold text-lg shadow-xl flex items-center justify-center gap-3 group transition-all
                                    ${missionProgress === 100
                                        ? 'bg-green-500 text-white cursor-default opacity-90'
                                        : 'bg-brand-orange text-white hover:bg-brand-orange/90'
                                    }
                                `}
                            >
                                {buttonText}
                                {missionProgress < 100 && (
                                    <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
                                )}
                            </button>
                        </section>

                        {/* RIGHT: Focus Split Charts (Moved here from bottom) */}
                        <section className="lg:col-span-1 space-y-6">
                            {plan.focusSplit.osp && (
                                <div className="bg-card rounded-3xl p-6 border border-border shadow-sm">
                                    <div className="flex items-center gap-3 mb-6">
                                        <BarChart3 className="w-6 h-6 text-brand-orange" />
                                        <h3 className="text-xl font-bold">Oblast OSP</h3>
                                    </div>
                                    <div className="space-y-4">
                                        {[
                                            { label: 'Logika', value: plan.focusSplit.osp.logic, color: 'bg-blue-500' },
                                            { label: 'Text', value: plan.focusSplit.osp.reading, color: 'bg-purple-500' },
                                            { label: 'Data', value: plan.focusSplit.osp.data, color: 'bg-orange-500' }
                                        ].map(item => (
                                            <div key={item.label} className="space-y-1">
                                                <div className="flex justify-between text-sm font-medium">
                                                    <span>{item.label}</span>
                                                    <span>{item.value}%</span>
                                                </div>
                                                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                                                    <div className={`${item.color} h-full rounded-full transition-all duration-1000`} style={{ width: `${item.value}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {plan.focusSplit.zsv && (
                                <div className="bg-card rounded-3xl p-6 border border-border shadow-sm">
                                    <div className="flex items-center gap-3 mb-6">
                                        <BarChart3 className="w-6 h-6 text-brand-orange" />
                                        <h3 className="text-xl font-bold">Oblast ZSV</h3>
                                    </div>
                                    <div className="space-y-4">
                                        {[
                                            { label: 'Filosofie', value: plan.focusSplit.zsv.filosofie, color: 'bg-emerald-500' },
                                            { label: 'Sociologie', value: plan.focusSplit.zsv.sociologie, color: 'bg-pink-500' },
                                            { label: 'Politologie', value: plan.focusSplit.zsv.politologie, color: 'bg-indigo-500' },
                                            { label: 'Ekonomie', value: plan.focusSplit.zsv.ekonomie, color: 'bg-amber-500' }
                                        ].map(item => (
                                            <div key={item.label} className="space-y-1">
                                                <div className="flex justify-between text-sm font-medium">
                                                    <span>{item.label}</span>
                                                    <span>{item.value}%</span>
                                                </div>
                                                <div className="w-full bg-secondary h-2 rounded-full overflow-hidden">
                                                    <div className={`${item.color} h-full rounded-full transition-all duration-1000`} style={{ width: `${item.value}%` }} />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default MyPlanPage;
